// src/pages/Automations/components/TemplateEditor.jsx
// Modal for editing SMS and email templates (Light theme)

import React, { useState, useEffect } from 'react';
import { X, Mail, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { getTriggerByKey } from '../constants/triggers';

export function TemplateEditor({
  isOpen,
  onClose,
  trigger,
  template,
  onSave,
  saving,
  replaceVariables,
  availableVariables
}) {
  const [activeTab, setActiveTab] = useState('sms');
  const [smsContent, setSmsContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');

  useEffect(() => {
    if (isOpen && trigger) {
      if (trigger.channels.includes('sms')) {
        setActiveTab('sms');
      } else if (trigger.channels.includes('email')) {
        setActiveTab('email');
      }

      // Get default templates from trigger definitions
      const triggerDef = getTriggerByKey(trigger.key);
      const defaultTemplates = triggerDef?.defaultTemplates || {};

      if (template) {
        // Use saved template or fall back to defaults
        setSmsContent(template.sms?.content || defaultTemplates.sms?.content || getDefaultSMS(trigger));
        setEmailSubject(template.email?.subject || defaultTemplates.email?.subject || getDefaultEmailSubject(trigger));
        setEmailContent(template.email?.content || defaultTemplates.email?.content || getDefaultEmailContent(trigger));
      } else {
        // Use default templates from trigger definitions
        setSmsContent(defaultTemplates.sms?.content || getDefaultSMS(trigger));
        setEmailSubject(defaultTemplates.email?.subject || getDefaultEmailSubject(trigger));
        setEmailContent(defaultTemplates.email?.content || getDefaultEmailContent(trigger));
      }
    }
  }, [isOpen, trigger, template]);

  const getDefaultSMS = (t) => {
    return `Hi {ClientFirstName}! It's {AgentName}. ${t?.explanation || 'Checking in to see how you\'re doing.'} Feel free to reach out if you have any questions! {AgentPhone}`;
  };

  const getDefaultEmailSubject = (t) => {
    return t?.name || 'Message from Your Medicare Agent';
  };

  const getDefaultEmailContent = (t) => {
    return `Hi {ClientFirstName},

${t?.explanation || 'I wanted to reach out and check in with you.'}

If you have any questions or concerns, please don't hesitate to contact me.

Best regards,
{AgentName}
{AgentPhone}
{AgentEmail}`;
  };

  const insertVariable = (variable) => {
    if (activeTab === 'sms') {
      setSmsContent(prev => prev + variable);
    } else {
      setEmailContent(prev => prev + variable);
    }
  };

  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const handleSave = async () => {
    const updates = {};

    if (trigger.channels.includes('sms')) {
      updates.sms = { content: smsContent };
    }
    if (trigger.channels.includes('email')) {
      updates.email = { subject: emailSubject, content: emailContent };
    }

    try {
      await onSave(trigger.key, updates);
      // Show confirmation popup
      setShowSaveConfirmation(true);
      // Auto-hide and close after 1.5 seconds
      setTimeout(() => {
        setShowSaveConfirmation(false);
        onClose();
      }, 1500);
    } catch (error) {
      // Error handling is done in onSave
      console.error('Save failed:', error);
    }
  };

  if (!isOpen || !trigger) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Save Confirmation Popup */}
      {showSaveConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
          <div className="bg-green-600 text-white px-8 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-bounce">
            <CheckCircle className="w-6 h-6" />
            <span className="font-bold text-lg">Template Saved!</span>
          </div>
        </div>
      )}

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Edit: {trigger.name}</h2>
            <p className="text-sm text-gray-500 mt-1">{trigger.explanation}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        {trigger.channels.length > 1 && (
          <div className="flex border-b border-gray-200 flex-shrink-0">
            {trigger.channels.includes('sms') && (
              <button
                onClick={() => setActiveTab('sms')}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                  activeTab === 'sms'
                    ? 'text-amber-600 border-b-2 border-amber-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                SMS
              </button>
            )}
            {trigger.channels.includes('email') && (
              <button
                onClick={() => setActiveTab('email')}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                  activeTab === 'email'
                    ? 'text-amber-600 border-b-2 border-amber-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* SMS Tab */}
          {activeTab === 'sms' && trigger.channels.includes('sms') && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  SMS Message
                </label>
                <textarea
                  value={smsContent}
                  onChange={(e) => setSmsContent(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                  placeholder="Enter your SMS message..."
                />
              </div>

              {/* Preview */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Preview</label>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-gray-800 text-sm whitespace-pre-wrap">
                    {replaceVariables(smsContent)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && trigger.channels.includes('email') && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Email subject..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Email Body
                </label>
                <textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none font-mono text-sm"
                  placeholder="Enter your email content..."
                />
              </div>

              {/* Preview */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Preview</label>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-amber-700 font-medium text-sm mb-2">
                    {replaceVariables(emailSubject)}
                  </p>
                  <p className="text-gray-800 text-sm whitespace-pre-wrap">
                    {replaceVariables(emailContent)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Variables */}
          <div className="mt-6">
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              Insert Variable
            </label>
            <div className="flex flex-wrap gap-2">
              {availableVariables.map((v) => (
                <button
                  key={v.key}
                  onClick={() => insertVariable(v.key)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-amber-700 text-xs font-mono rounded-lg transition-colors border border-gray-200"
                  title={`Preview: ${v.preview}`}
                >
                  {v.key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <Send className="w-4 h-4" />
            Send Test
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateEditor;
